/* eslint-disable react/no-multi-comp */
import React from 'react';
import { matchPath } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { List, Typography } from '@material-ui/core';

import { NavigationListItem } from './components';
import { useTranslation } from "react-i18next";
import { useRouter, hasAccess } from "utils";
import { useSelector } from "react-redux";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3)
  }
}));

const NavigationList = props => {
  const { pages, userRoles, ...rest } = props;

  return (
    <List>
      {pages.reduce(
        (items, page) => reduceChildRoutes({ items, page, userRoles, ...rest }),
        []
      )}
    </List>
  );
};

NavigationList.propTypes = {
  collapsed: PropTypes.bool,
  depth: PropTypes.number,
  pages: PropTypes.array
};

const reduceChildRoutes = props => {
  const { router, items, page, userRoles, depth, collapsed, onExpand } = props;
  const { availableForRoles } = page;

  if (availableForRoles && !hasAccess(availableForRoles, userRoles)) {
    return items;
  }

  if (page.children) {
    const open = matchPath(router.location.pathname, {
      path: page.href,
      exact: false
    });

    items.push(
      <NavigationListItem
        collapsed={collapsed}
        depth={depth}
        icon={page.icon}
        key={page.title}
        label={page.label}
        onExpand={onExpand}
        open={Boolean(open)}
        title={page.title}
      >
        <NavigationList
          collapsed={collapsed}
          depth={depth + 1}
          onExpand={onExpand}
          pages={page.children}
          router={router}
          userRoles={userRoles}
        />
      </NavigationListItem>
    );
  } else {
    items.push(
      <NavigationListItem
        collapsed={collapsed}
        depth={depth}
        href={page.href}
        icon={page.icon}
        key={page.title}
        label={page.label}
        title={page.title}
      />
    );
  }

  return items;
};

const Navigation = props => {
  const { title, pages, className, availableForRoles, collapsed, onExpand, component: Component, ...rest } = props;
  const user = useSelector(state => state.userSession.user);
  const classes = useStyles();
  const router = useRouter();
  const [t] = useTranslation();

  return (
    hasAccess(availableForRoles, user.roles) &&
      <Component
          {...rest}
          className={clsx(classes.root, className)}
      >
        {title && !collapsed && <Typography variant="overline">{t(title)}</Typography>}
        <NavigationList
            collapsed={collapsed}
            depth={0}
            onExpand={onExpand}
            pages={pages}
            router={router}
            userRoles={user.roles}
        />
      </Component>
  );
};

Navigation.propTypes = {
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  onExpand: PropTypes.func,
  component: PropTypes.any,
  pages: PropTypes.array.isRequired,
  title: PropTypes.string
};

Navigation.defaultProps = {
  collapsed: false,
  component: 'nav'
};

export default Navigation;
